import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiPhone, FiCheck, FiX } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { auth as authApi } from '../../api';
import toast from 'react-hot-toast';

// ============================================================
// REGISTER PAGE
// ============================================================
export function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', confirm: '', first_name: '', last_name: '', phone: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const pwdChecks = {
    length: form.password.length >= 8,
    upper: /[A-Z]/.test(form.password),
    lower: /[a-z]/.test(form.password),
    number: /\d/.test(form.password),
  };
  const pwdValid = Object.values(pwdChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwdValid) { toast.error('Password does not meet requirements'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await register(form);
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 bg-navy rounded-xl flex items-center justify-center">
            <span className="text-white font-black">A</span>
          </div>
          <span className="font-bold text-xl text-navy">AssessHub</span>
        </div>
        <div className="card p-8 animate-slide-up">
          <h2 className="text-2xl font-black text-gray-900 mb-1">Create account</h2>
          <p className="text-gray-500 text-sm mb-6">Register as a candidate to take assessments</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <div className="relative"><FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                  <input className="input pl-9" placeholder="John" value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} required /></div>
              </div>
              <div>
                <label className="label">Last Name</label>
                <div className="relative"><FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                  <input className="input pl-9" placeholder="Doe" value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} required /></div>
              </div>
            </div>
            <div>
              <label className="label">Email</label>
              <div className="relative"><FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input type="email" className="input pl-9" placeholder="you@example.com" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required /></div>
            </div>
            <div>
              <label className="label">Phone (optional)</label>
              <div className="relative"><FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input className="input pl-9" placeholder="+91 9876543210" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
            </div>
            <div>
              <label className="label">Password</label>
              <div className="relative"><FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input type={showPwd ? 'text' : 'password'} className="input pl-9 pr-9" placeholder="••••••••" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} required />
                <button type="button" onClick={() => setShowPwd(s=>!s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPwd ? <FiEyeOff size={15} /> : <FiEye size={15} />}</button>
              </div>
              {form.password && (
                <div className="mt-2 grid grid-cols-2 gap-1">
                  {[['length','8+ characters'],['upper','Uppercase letter'],['lower','Lowercase letter'],['number','Number']].map(([k,v]) => (
                    <div key={k} className={`flex items-center gap-1.5 text-xs ${pwdChecks[k] ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {pwdChecks[k] ? <FiCheck size={11} /> : <FiX size={11} />} {v}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="label">Confirm Password</label>
              <div className="relative"><FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input type="password" className="input pl-9" placeholder="••••••••" value={form.confirm} onChange={e => setForm(f => ({...f, confirm: e.target.value}))} required /></div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3 mt-2">
              {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
          <p className="text-center text-sm text-gray-500 mt-4">
            Already have an account? <Link to="/login" className="text-navy font-bold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// VERIFY EMAIL PAGE
// ============================================================
export function VerifyEmailPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    authApi.verifyEmail(token).then(() => setStatus('success')).catch(() => setStatus('error'));
  }, [token]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="card p-10 text-center max-w-md w-full animate-slide-up">
        {status === 'loading' && <div className="w-12 h-12 border-4 border-navy/20 border-t-navy rounded-full animate-spin mx-auto mb-4" />}
        {status === 'success' && <>
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><FiCheck size={28} className="text-emerald-600" /></div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Email Verified!</h2>
          <p className="text-gray-500 mb-6">Your account is ready. You can now sign in.</p>
          <Link to="/login" className="btn-primary">Go to Login</Link>
        </>}
        {status === 'error' && <>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><FiX size={28} className="text-red-600" /></div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Verification Failed</h2>
          <p className="text-gray-500 mb-6">The link is invalid or expired.</p>
          <Link to="/login" className="btn-secondary">Back to Login</Link>
        </>}
      </div>
    </div>
  );
}

// ============================================================
// FORGOT PASSWORD PAGE
// ============================================================
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await authApi.forgotPassword(email); setSent(true); }
    catch { toast.error('Failed to send email'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="card p-8 w-full max-w-md animate-slide-up">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-navy rounded-xl flex items-center justify-center"><span className="text-white font-black">A</span></div>
          <span className="font-bold text-xl text-navy">AssessHub</span>
        </div>
        {sent ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><FiMail size={28} className="text-emerald-600" /></div>
            <h2 className="text-xl font-black mb-2">Check your email</h2>
            <p className="text-gray-500 text-sm">We sent a reset link to <strong>{email}</strong>. It expires in 1 hour.</p>
          </div>
        ) : (<>
          <h2 className="text-2xl font-black mb-2">Reset password</h2>
          <p className="text-gray-500 text-sm mb-6">Enter your email and we'll send you a reset link.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="label">Email</label>
              <div className="relative"><FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input type="email" className="input pl-9" value={email} onChange={e => setEmail(e.target.value)} required /></div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        </>)}
        <p className="text-center text-sm text-gray-500 mt-4">
          <Link to="/login" className="text-navy font-bold hover:underline">← Back to Login</Link>
        </p>
      </div>
    </div>
  );
}

// ============================================================
// RESET PASSWORD PAGE
// ============================================================
export function ResetPasswordPage() {
  const { token } = useParams();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authApi.resetPassword({ token, password: form.password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) { toast.error(err.response?.data?.message || 'Reset failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="card p-8 w-full max-w-md animate-slide-up">
        <h2 className="text-2xl font-black mb-6">Set new password</h2>
        {done ? (
          <div className="text-center"><div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3"><FiCheck className="text-emerald-600" size={20} /></div>
          <p className="text-gray-600">Password reset! Redirecting to login...</p></div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="label">New Password</label>
              <input type="password" className="input" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} required minLength={8} /></div>
            <div><label className="label">Confirm Password</label>
              <input type="password" className="input" value={form.confirm} onChange={e => setForm(f => ({...f, confirm: e.target.value}))} required /></div>
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default RegisterPage;
